import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, ArrowRight, Workflow, ShieldCheck, FolderTree, Sparkles,
  Search, Stethoscope, PenTool, Hammer, TrendingUp, Building2, Cpu, Database, CheckCircle2
} from 'lucide-react';

const EMAIL = 'anna200224@hotmail.com';
const mailto = (subject) => `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}`;

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const services = [
  {
    num: '01', tag: 'TRANSFORM', title: 'Business & Process Transformation',
    icon: <Workflow className="text-brand-primary" size={24} />,
    intro: 'Turn informal, undocumented or inefficient ways of working into clear, scalable processes.',
    items: ['Process discovery and mapping', 'Current-state assessment', 'Process optimisation', 'Workflow design', 'Governance and ownership', 'RACI and responsibility mapping', 'Decision frameworks', 'Process documentation', 'Transformation roadmaps', 'Change implementation'],
    outcome: 'Clearer processes, defined ownership and less dependency on individual knowledge.',
  },
  {
    num: '02', tag: 'SECURE', title: 'ISO 27001 & Governance',
    icon: <ShieldCheck className="text-brand-primary" size={24} />,
    intro: 'Build practical governance and security frameworks that become part of everyday operations — rather than a collection of documents created for an audit.',
    items: ['ISO 27001 gap assessment', 'ISMS implementation', 'Risk assessment and risk registers', 'Statement of Applicability', 'Information security policies and procedures', 'Access and asset governance', 'People and supplier governance', 'Security awareness', 'Incident management', 'Compliance evidence', 'Internal audit preparation', 'External audit readiness', 'GDPR operational implementation', 'Regulatory requirements tracking'],
    outcome: "A practical governance framework embedded into the organisation's real processes.",
  },
  {
    num: '03', tag: 'DIGITISE', title: 'Digital Workplace & Information Management',
    icon: <FolderTree className="text-brand-primary" size={24} />,
    intro: 'Bring structure to fragmented information, systems and ways of working.',
    items: ['Microsoft 365 transformation', 'SharePoint information architecture', 'Document management', 'Metadata and taxonomy', 'Data and document migration', 'Information governance', 'Access and permissions', 'Digital workflow design', 'Knowledge management', 'Digital workplace optimisation'],
    outcome: 'A structured digital environment where people can find, manage and use information effectively.',
  },
  {
    num: '04', tag: 'OPTIMISE', title: 'AI & Automation',
    icon: <Sparkles className="text-brand-primary" size={24} />,
    intro: 'Use AI and automation where they genuinely improve the way people work.',
    items: ['Automation opportunity assessment', 'AI use-case discovery', 'AI readiness', 'Internal AI assistants', 'Knowledge retrieval agents', 'Document generation workflows', 'Power Automate workflows', 'AI + SharePoint solutions', 'Process automation', 'Human-in-the-loop workflows'],
    note: "I don't automate processes for the sake of automation. I first understand the process, simplify it where possible, and then decide whether technology should be involved.",
    outcome: 'Less repetitive work, better access to information and technology that solves a real business problem.',
  },
];

const audience = [
  { icon: <TrendingUp size={20} />, title: 'Growing organisations', text: 'Your processes have evolved organically and are becoming difficult to manage or scale.' },
  { icon: <Cpu size={20} />, title: 'Technology companies', text: 'Technology and product development have grown faster than governance, documentation and operational processes.' },
  { icon: <ShieldCheck size={20} />, title: 'Preparing for ISO 27001', text: 'You need more than policies. You need processes, ownership, controls and evidence that actually work.' },
  { icon: <Workflow size={20} />, title: 'Going through transformation', text: "You know things need to change but don't have the internal capacity to design and drive the change." },
  { icon: <Building2 size={20} />, title: 'Post-acquisition organisations', text: 'A new company or team needs to be brought into a consistent governance and operating model.' },
  { icon: <Database size={20} />, title: 'Information-heavy organisations', text: "Critical information is scattered across multiple systems, repositories and people's individual knowledge." },
];

const steps = [
  { num: '01', icon: <Search size={20} />, title: 'Understand', text: 'I start with how your organisation actually works, not how a process document says it works. I talk to the people involved, understand the technology and identify the real constraints.' },
  { num: '02', icon: <Stethoscope size={20} />, title: 'Diagnose', text: 'I identify what is working, what isn\u2019t, where the risks are, where time is being lost, what depends on individual knowledge, and what should change first.' },
  { num: '03', icon: <PenTool size={20} />, title: 'Design', text: 'I turn the findings into a practical solution covering the right combination of processes, governance, systems, information, controls and workflows.' },
  { num: '04', icon: <Hammer size={20} />, title: 'Implement', text: "I don't stop at recommendations. I work alongside the organisation to implement the changes, establish processes, configure solutions and create the evidence needed to demonstrate that they work." },
  { num: '05', icon: <TrendingUp size={20} />, title: 'Improve', text: 'Once the foundations are in place, we identify what can be simplified, automated or improved next.' },
];

const engagements = [
  { title: 'Transformation Health Check', duration: 'Typical duration: 2\u20133 weeks', intro: 'A focused assessment of your processes, technology, information and governance.', label: 'Deliverables', items: ['Current-state assessment', 'Key findings and risks', 'Prioritised recommendations', 'Transformation roadmap'], cta: 'Discuss a Health Check', subject: 'Consulting — Transformation Health Check' },
  { title: 'Transformation Project', duration: 'Typical duration: 1\u20136 months', intro: 'A defined engagement focused on solving a specific organisational problem.', label: 'May include', items: ['Discovery', 'Process design', 'Governance', 'Technology', 'Implementation', 'Documentation', 'Knowledge transfer'], cta: 'Discuss a Project', subject: 'Consulting — Transformation Project' },
  { title: 'ISO 27001 Implementation', duration: null, intro: 'Practical support to establish an ISMS and prepare an organisation for certification.', label: 'May include', items: ['Gap assessment', 'ISMS framework', 'Risk management', 'Policies and controls', 'Evidence framework', 'Audit readiness'], cta: 'Discuss ISO 27001', subject: 'Consulting — ISO 27001 Implementation' },
  { title: 'Fractional Transformation & Governance Lead', duration: null, intro: 'For organisations that need senior transformation expertise without hiring a permanent transformation leader. A flexible ongoing engagement providing strategic direction and hands-on delivery across transformation, governance, technology and process improvement.', label: null, items: [], cta: 'Discuss a Fractional Engagement', subject: 'Consulting — Fractional Engagement' },
];

const Consulting = () => {
  return (
    <div className="min-h-screen bg-bg-page" data-testid="consulting-page">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-50 bg-bg-page/90 backdrop-blur-md border-b border-border-medium">
        <div className="container mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 nav-link" data-testid="consulting-back-link">
            <ArrowLeft size={18} />
            Portfolio
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => scrollTo('services')} className="nav-link" data-testid="consulting-nav-services">Services</button>
            <button onClick={() => scrollTo('how')} className="nav-link">How I Work</button>
            <button onClick={() => scrollTo('engagements')} className="nav-link">Engagements</button>
            <button onClick={() => scrollTo('approach')} className="nav-link">Approach</button>
          </div>
          <a href={mailto('Consulting enquiry')} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-primary text-white text-sm font-medium hover:bg-brand-hover transition-colors" data-testid="consulting-topbar-cta">
            Start a conversation
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-border-light">
        <div className="container mx-auto px-8 py-24 max-w-5xl">
          <p className="caption text-brand-primary mb-6 tracking-[0.15em]">BUSINESS TRANSFORMATION · GOVERNANCE · DIGITAL</p>
          <h1 className="heading-1 text-text-primary mb-8 max-w-3xl">Transform the way your organisation works.</h1>
          <p className="body-large text-text-primary mb-6 max-w-3xl">
            I help growing organisations turn fragmented processes, undocumented knowledge and operational complexity into structured, secure and scalable ways of working.
          </p>
          <p className="body-medium max-w-3xl mb-10">
            I work across business transformation, ISO 27001, governance, digital workplace and practical automation — connecting strategy with implementation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href={mailto('Consulting enquiry')} className="btn-primary" data-testid="consulting-hero-cta">Start a conversation</a>
            <button onClick={() => scrollTo('services')} className="btn-secondary" data-testid="consulting-hero-explore">Explore services</button>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="container mx-auto px-8 py-24 max-w-5xl">
        <h2 className="heading-2 text-text-primary mb-10 max-w-2xl">When growth exposes the cracks.</h2>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-5">
            <p className="body-medium text-text-primary">Many organisations don't have a technology problem.<br />They have a how-we-work problem.</p>
            <p className="body-medium">
              Processes have evolved organically. Critical knowledge lives in people's heads. Documents are spread across different systems. Responsibilities aren't always clear. Manual work grows. Compliance becomes harder. And the organisation becomes increasingly dependent on a few people who simply know how everything works.
            </p>
            <p className="body-medium text-text-primary">This can work for a while.<br />Until the organisation grows.</p>
          </div>
          <div className="team-card p-8 lg:mt-2">
            <p className="heading-4 text-brand-primary leading-snug">
              I help organisations turn institutional knowledge into organisational infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT I DO */}
      <section id="services" className="bg-bg-card border-y border-border-light">
        <div className="container mx-auto px-8 py-24 max-w-6xl">
          <div className="mb-16 max-w-2xl">
            <p className="caption text-brand-primary mb-4">WHAT I DO</p>
            <h2 className="heading-2 text-text-primary">Four areas. One goal.<br />Make the organisation work better.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((s, i) => (
              <div key={i} className="team-card p-8 flex flex-col" data-testid={`consulting-service-${i}`}>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    {s.icon}
                    <span className="caption text-brand-primary">{s.tag}</span>
                  </div>
                  <span className="heading-4 text-border-strong">{s.num}</span>
                </div>
                <h3 className="heading-4 text-text-primary mb-3">{s.title}</h3>
                <p className="body-small text-text-secondary mb-6">{s.intro}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {s.items.map((it, j) => (
                    <span key={j} className="px-3 py-1 rounded-full bg-brand-soft text-brand-primary text-xs font-medium border border-indigo-100">{it}</span>
                  ))}
                </div>
                {s.note && (
                  <p className="body-small text-text-primary italic border-l-2 border-brand-primary pl-4 mb-6">{s.note}</p>
                )}
                <div className="mt-auto pt-5 border-t border-border-light">
                  <p className="caption text-brand-primary mb-1">Outcome</p>
                  <p className="body-small text-text-primary">{s.outcome}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO I WORK WITH */}
      <section id="who" className="container mx-auto px-8 py-24 max-w-6xl">
        <h2 className="heading-2 text-text-primary mb-16 max-w-3xl">Built for organisations that have outgrown their informal ways of working.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {audience.map((a, i) => (
            <div key={i} className="team-card p-7" data-testid={`consulting-audience-${i}`}>
              <div className="text-brand-primary mb-4">{a.icon}</div>
              <h3 className="heading-6 text-text-primary mb-2">{a.title}</h3>
              <p className="body-small text-text-secondary">{a.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW I WORK */}
      <section id="how" className="bg-bg-card border-y border-border-light">
        <div className="container mx-auto px-8 py-24 max-w-5xl">
          <h2 className="heading-2 text-text-primary mb-16 max-w-2xl">From understanding the problem to making the change.</h2>
          <div className="space-y-6">
            {steps.map((s, i) => (
              <div key={i} className="team-card p-7 flex items-start gap-6" data-testid={`consulting-step-${i}`}>
                <span className="heading-3 text-border-strong flex-shrink-0 w-14">{s.num}</span>
                <div>
                  <div className="flex items-center gap-2 mb-2 text-brand-primary">
                    {s.icon}
                    <h3 className="heading-5 text-text-primary">{s.title}</h3>
                  </div>
                  <p className="body-small text-text-secondary">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODELS */}
      <section id="engagements" className="container mx-auto px-8 py-24 max-w-6xl">
        <div className="mb-16 max-w-2xl">
          <h2 className="heading-2 text-text-primary mb-4">Flexible by design.</h2>
          <p className="body-medium">Engagement models, not rigid packages — shaped around the problem you need to solve.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {engagements.map((e, i) => (
            <div key={i} className="team-card p-8 flex flex-col" data-testid={`consulting-engagement-${i}`}>
              <h3 className="heading-4 text-text-primary mb-2">{e.title}</h3>
              {e.duration && <p className="caption text-brand-primary mb-4">{e.duration}</p>}
              <p className="body-small text-text-secondary mb-6">{e.intro}</p>
              {e.label && (
                <div className="mb-6">
                  <p className="caption text-brand-primary mb-3">{e.label}</p>
                  <ul className="space-y-2">
                    {e.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <CheckCircle2 size={15} className="text-brand-primary flex-shrink-0 mt-1" />
                        <span className="body-small text-text-primary">{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <a href={mailto(e.subject)} className="mt-auto inline-flex items-center gap-2 caption text-brand-primary hover:text-brand-hover transition-colors" data-testid={`consulting-engagement-cta-${i}`}>
                <span>{e.cta}</span>
                <ArrowRight size={16} />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* MY APPROACH */}
      <section id="approach" className="bg-bg-card border-y border-border-light">
        <div className="container mx-auto px-8 py-24 max-w-4xl">
          <h2 className="heading-2 text-text-primary mb-10">Transformation without the theatre.</h2>
          <div className="space-y-5 mb-10">
            <p className="body-medium">I don't believe in transformation for transformation's sake.</p>
            <p className="body-medium">Technology should solve a business problem. Processes should make work easier, not create unnecessary bureaucracy. Governance should protect the organisation without preventing people from getting things done. And compliance should become part of the way an organisation operates — not a project everyone panics about before an audit.</p>
          </div>
          <p className="heading-4 text-brand-primary">The goal isn't to create more processes.<br />It's to create a better organisation.</p>
        </div>
      </section>

      {/* WHY ME */}
      <section id="why" className="container mx-auto px-8 py-24 max-w-4xl">
        <h2 className="heading-2 text-text-primary mb-10 max-w-3xl">Strategy is only useful when it survives contact with reality.</h2>
        <div className="space-y-5 mb-10">
          <p className="body-medium">My experience spans technology product development, business transformation, governance, ISO 27001, digital workplace transformation, systems implementation and cross-functional programme delivery.</p>
          <p className="body-medium">I've worked across technical and non-technical environments, bringing together developers, product teams, operations, finance, legal, procurement, HR and senior leadership.</p>
          <p className="body-medium">That means I don't approach transformation from a single department's perspective. I look at the organisation as a system.</p>
        </div>
        <div className="team-card p-8">
          <p className="heading-4 text-brand-primary">I combine strategic thinking with hands-on implementation.</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="contact-cta" className="bg-[#020617]">
        <div className="container mx-auto px-8 py-24 max-w-4xl text-center">
          <h2 className="heading-2 text-white mb-6">Start with the problem. Not the technology.</h2>
          <p className="body-medium text-slate-300 max-w-2xl mx-auto mb-4">
            If something in your organisation has become unnecessarily complicated, difficult to scale, dependent on individual knowledge or simply no longer works the way it should — let's talk.
          </p>
          <p className="body-medium text-slate-400 max-w-2xl mx-auto mb-10">
            You don't need to know what solution you need before starting the conversation. That's part of the work.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={mailto('Consulting enquiry')} className="btn-primary" data-testid="consulting-final-cta">Start a conversation</a>
            <Link to="/#experience" className="btn-secondary" data-testid="consulting-view-experience">View my experience</Link>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="bg-bg-page border-t border-border-medium">
        <div className="container mx-auto px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="caption text-text-secondary">© {new Date().getFullYear()} Anna Krat</span>
          <div className="flex items-center gap-6">
            <Link to="/" className="nav-link">Portfolio</Link>
            <a href="/Anna-Krat-CV.pdf" download="Anna-Krat-CV.pdf" className="inline-flex items-center gap-2 nav-link"><Download size={16} /> CV</a>
            <a href={mailto('Consulting enquiry')} className="nav-link">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Consulting;
